import { StaticChip } from "@/components/ui/chip";
import { MuiAccessTimeIcon, NotRecommendedIcon } from "@/components/ui/mui-status-icons";
import { MuiTooltip } from "@/components/ui/mui-tooltip";
import {
  itemDetailStatusBadge,
  itemEiTiers,
  itemShowsEiMark,
  itemStatusBadge,
  type CatalogDetailBadgeInput,
  type CatalogItemBadgeInput,
  type CatalogStatusBadgeKind,
} from "@/lib/catalog-item-badges";
import { cn } from "@/lib/utils";

// One item's status badge, in each of the three shapes the donor draws it.
//
// Which badge appears is `itemStatusBadge`'s decision (the details page asks a
// shorter chain, `itemDetailStatusBadge`); this file paints it — and the
// *painting* is where the views differ, which is why there are three
// components here rather than one with a prop:
//
//   - `ItemStatusLabel` is the table cell's chip: in the flow of the row, no
//     tooltip on Pending, sentence case.
//   - `ItemStatusOverlay` is the card and row's corner badge: absolutely
//     positioned over the top-left of the card, title case. Core is a flush
//     ribbon with one rounded corner; the other two are inset chips with an
//     icon and a tooltip. That split is the donor's — `CoreItemBadge` and
//     `ItemStatusChip` are separate components with different geometry.
//   - `ItemDetailStatusBadge` is the details page's header badge: in the flow
//     of the header row, a plain pill for Core, and Pending *with* its tooltip.
//
// `ItemEiTierChips` is drawn by the card and by the details page's header —
// both are the donor's one `EITiers` component, on the gate `itemEiTiers`
// states (suppressed while the item is pending, in both places).

/** Why an item is flagged Not Recommended, and where the alternatives are. */
const NOT_RECOMMENDED_SPREADSHEET_URL =
  "https://lennarcorporation.sharepoint.com/:x:/r/sites/LennarResearchandBuildingTechnology/_layouts/15/Doc.aspx?sourcedoc=%7BFACD822C-39B3-4961-94DD-D443A33EDCA5%7D&file=3256031239Lennar-Recomended-Products--LAP.xlsx&action=default&mobileredirect=true";

function NotRecommendedTooltip() {
  return (
    <>
      This item has not passed quality control testing. Please refer to the recommended products
      found in this{" "}
      <a
        href={NOT_RECOMMENDED_SPREADSHEET_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
      >
        spreadsheet
      </a>
      .
    </>
  );
}

const PENDING_TOOLTIP = "Item is being reviewed by the DigitalOps team.";

/**
 * The Not Recommended label's `closeDelay` (`item-not-recommended.tsx:37`),
 * the one donor tooltip that lingers. The corner badge's donor passes none.
 */
const NOT_RECOMMENDED_LEAVE_DELAY_MS = 200;

/**
 * The ground and ink each status chip carries, in every shape — the donor's
 * `backgroundColor`/`textColor` props: `primary.main` under white for Core,
 * `error.pale` and `pending.main` under black for the other two.
 */
const CHIP_COLORS: Record<CatalogStatusBadgeKind, string> = {
  core: "bg-lennar-blue-600 text-white",
  "not-recommended": "bg-catalog-not-recommended text-black",
  pending: "bg-catalog-pending text-black",
};

/**
 * The leading icon, where the donor's chip has one. Core has none.
 *
 * The donor's own glyphs at the small chip's 18px slot: MUI's `AccessTime`,
 * forced to `#E38D0D` (`item-status-chip.tsx`, `item-pending-label.tsx`), and
 * its `NotRecommendedIconSvg`. Both are `aria-hidden` through the chip's icon
 * slot: each sits directly beside the word it decorates, so announcing it would
 * say the status twice.
 */
function statusIcon(kind: CatalogStatusBadgeKind) {
  if (kind === "pending") return <MuiAccessTimeIcon className="text-catalog-pending-icon" />;
  if (kind === "not-recommended") return <NotRecommendedIcon />;
  return undefined;
}

/**
 * One status chip: MUI's small filled `Chip` at the donor's `fontSize: '0.8rem'`
 * — the one `sx` both `ItemStatusChip` and `ItemStatusLabelInternal` pass.
 */
function StatusChip({
  kind,
  label,
  className,
}: {
  kind: CatalogStatusBadgeKind;
  label: string;
  className?: string;
}) {
  return (
    <StaticChip
      size="small"
      label={label}
      icon={statusIcon(kind)}
      className={cn(CHIP_COLORS[kind], "text-[0.8rem]", className)}
    />
  );
}

interface ItemBadgeProps {
  item: CatalogItemBadgeInput;
  className?: string;
}

/**
 * The two painters the **anonymous product page** reuses.
 *
 * Their rules never consult `isNotRecommended` and the public payload does not
 * carry it, so requiring the wider input would make the page invent the flag —
 * see `CatalogDetailBadgeInput`.
 */
interface ItemDetailBadgeProps {
  item: CatalogDetailBadgeInput;
  className?: string;
}

/**
 * Where the EI mark sits, by screen.
 *
 * The donor places it twice, with different numbers each time — `top: 75,
 * left: 0, height: 50` inside a card's image column (`item-card.tsx`), and
 * `bottom: 80, left: 8, height: 65` over the details page's much larger slot
 * (`Lightbox/Images.tsx:43-45`). Both are the donor's decisions about *this*
 * component, so both live in it rather than arriving as offsets a page picked.
 */
const EI_LOGO_PLACEMENTS = {
  // The height is the one responsive value in the donor's placement: `height:
  // { xs: 40, sm: 50 }` against a flat `top: 75` (`item-card.tsx:208`).
  card: "top-[75px] left-0 h-[50px] max-sm:h-10",
  detail: "bottom-[80px] left-2 h-[65px]",
  /** The Add Variant dialog's preview card — `top: 170, left: 8, height: 65`
   * over its 250px hero (`AddVariantIsland/components/item-details.tsx:97`). */
  preview: "top-[170px] left-2 h-[65px]",
  /** The Item Search island's card — `bottom: 8, left: 8, height: 65` against
   * its `CardActionArea`, which holds only the 250px hero (`ItemCard.tsx`). */
  island: "bottom-2 left-2 h-[65px]",
} as const;

export type ItemEiLogoPlacement = keyof typeof EI_LOGO_PLACEMENTS;

/**
 * The table cell's status chip.
 *
 * Renders nothing at all for an ordinary item rather than an empty element: the
 * Status column is untitled and mostly blank in the donor, and a zero-height
 * placeholder in every other row would still hold the cell open.
 */
export function ItemStatusLabel({ item, className }: ItemBadgeProps) {
  const badge = itemStatusBadge(item, "label");
  if (badge === null) return null;

  const chip = <StatusChip kind={badge.kind} label={badge.label} className={className} />;

  // Only Not Recommended carries a tooltip here — the donor's table gives
  // `ItemPendingLabel` none, and a badge that explains itself in one view but
  // not another is the donor's own inconsistency, kept.
  return badge.kind === "not-recommended" ? (
    // **The table label's tooltip is not the chips' tooltip.** This painter's
    // donor is `ItemNotRecommendedLabel`, which passes `placement='top-start'`,
    // `showCallout` and no `maxWidth`
    // (`item-status-label/item-not-recommended.tsx:31-38`) — so it opens *above
    // and left-aligned*, with a callout, at MUI's full 300px. The two chip
    // painters below go through `ItemStatusChip` instead, which narrows to 180
    // and opens downward with no callout; reading them as one tooltip is what
    // put 180 and no arrow here.
    //
    // **And its `closeDelay={200}` is carried as it is** (#506): forwarded as
    // `leaveDelay` (`react-ui/src/tooltip/tooltip.tsx:80`), it holds the box
    // 200ms after the pointer leaves the label, and the box's own
    // `onMouseOver` cancels that close (`Tooltip.js:346-347,515`) — which is
    // what lets a reader reach the spreadsheet link in the body. This was a
    // named parity break on #254 while `MuiTooltip` had no leave timer; it is
    // parity now.
    //
    // No `asChild`: that would make the badge itself the trigger and drop the
    // wrapper; the button this renders is what carries the tooltip's focus
    // target instead, so the badge stays a plain `<span>` with no interactive
    // role it cannot honour.
    // **No cursor of its own.** Nothing in the donor paints one for these
    // badges: `item-status-chip.tsx` and `item-not-recommended.tsx` name no
    // cursor, `cursor: 'help'` appears nowhere in `timmy/src` or
    // `timmy/packages`, and the nearest `cursor: 'pointer'` is on the card's
    // name `Typography` (`ItemCard.tsx:340`), which is not an ancestor of
    // these. So the donor's badge shows whatever its ancestors do, which on
    // these screens is the ordinary arrow — the same reasoning is written out
    // at `add-variant-preview-card.tsx`.
    //
    // `Chip.js:88-89` sets `cursor: 'unset'`, which is worth reading
    // carefully rather than as "the arrow": `cursor` inherits, so `unset`
    // there means `inherit`. The two sides agree today because no ancestor
    // paints one, not because the donor pins it. Where they would part is an
    // ancestor that *does* — the donor's `<div>`/`<span>` would show it
    // through and the Radix `<button>` here would not, Chromium's UA sheet
    // pinning `cursor: default` on a button. That residual belongs to D8,
    // which already carries the trigger being a `<button>` at all.
    //
    // The donor's `disableInteractive={false}` needs no prop here: it is
    // MUI's default (`Tooltip.js:240`) and every `MuiTooltip` box is
    // interactive.
    <MuiTooltip
      title={<NotRecommendedTooltip />}
      side="top"
      align="start"
      arrow
      leaveDelayMs={NOT_RECOMMENDED_LEAVE_DELAY_MS}
    >
      {chip}
    </MuiTooltip>
  ) : (
    chip
  );
}

/**
 * The card and row's corner badge, drawn over the top-left of the card.
 *
 * The caller supplies `relative` on the card; this positions itself. Core is a
 * ribbon flush into the corner — one rounded corner, squared into the card's
 * own edge — and the other two are inset chips, exactly as modsy draws them.
 *
 * **`rule` picks the chain, not the paint.** The Add Variant dialog's preview
 * card draws these two shapes at these two positions — its `CoreItemBadge` is
 * the same 85px ribbon and its `ItemStatusPendingChip` is the same
 * `ItemStatusChip`, which positions *itself* at `top: 10, left: 10`
 * (`components/item-status-chip`), so the Stack it sits in changes nothing about
 * where it lands. What differs is which badge wins: that card asks
 * `isCore && isApproved` and `!isApproved` and has no Not Recommended arm at all
 * (`AddVariantIsland/components/item-details.tsx:88-92, 119-131`), which is
 * `itemDetailStatusBadge`'s chain rather than the list's.
 *
 * One prop over two existing chains, rather than a fourth component with the
 * geometry copied into it: the two rules already live apart in `lib/` — see the
 * note on `itemDetailStatusBadge` — and it is only the *choice* between them
 * that belongs to the view.
 */
export function ItemStatusOverlay({
  item,
  rule = "list",
  selected = false,
  className,
}: ItemBadgeProps & {
  rule?: "list" | "detail";
  /**
   * The Item Search island's selected card (#798): its 4px primary border meets
   * the Core ribbon's top-left corner, so the ribbon squares it off —
   * `borderRadius: isSelected ? '0 0 4px 0' : '4px 0 4px 0'` (the island's
   * local `CoreItemBadge`, `ItemCard.tsx:221-236`). Core only; the chips sit
   * inset and never touch the border.
   */
  selected?: boolean;
}) {
  const badge = rule === "detail" ? itemDetailStatusBadge(item) : itemStatusBadge(item, "chip");
  if (badge === null) return null;

  if (badge.kind === "core") {
    return (
      <span
        data-testid="item-status-overlay"
        className={cn(
          // `CoreItemBadge`'s own box: a fixed 85px, body2 at its normal
          // weight, 4px/10px padding, and MUI's `borderRadius: '4px 0 4px 0'`
          // — rounded on the top-left and bottom-right only.
          "bg-lennar-blue-600 absolute top-0 left-0 z-10 w-[85px] rounded-tl-[4px] rounded-tr-none rounded-br-[4px] rounded-bl-none px-2.5 py-1 text-sm font-normal text-white",
          selected && "rounded-tl-none",
          className,
        )}
      >
        {badge.label}
      </span>
    );
  }

  return (
    <MuiTooltip
      title={badge.kind === "not-recommended" ? <NotRecommendedTooltip /> : PENDING_TOOLTIP}
      // **Interactive, as every box is, and no leave delay.** This painter's
      // donor is not the label's: the corner badge goes through `ItemStatusChip`
      // (`ItemCard.tsx:166` → `ItemNotRecommendedChip.tsx` →
      // `item-status-chip/item-status-chip.tsx`), which passes the wrapper no
      // `closeDelay` and no `disableInteractive` — so `leaveDelay` is 0 where
      // the label's is 200, and that is the whole difference between them.
      // `disableInteractive` is `false` on both, because MUI's default is
      // (`Tooltip.js:240`): the popper carries `onMouseOver` and
      // `handleMouseOver` clears the pending close (`:346-347,506,515-516,609`),
      // so a pointer crossing the 14px gap keeps this box up and reaches the
      // spreadsheet link inside it, at any speed (#506). An earlier pass here
      // said the opposite — that the link "is not mouse-reachable in the donor
      // either" — which read `leaveDelay: 0` as though it also settled
      // reachability. It does not; the two are separate props, and #479's
      // parity review found the conflation. What #254 ruled is the label's `closeDelay={200}`, not this.
      // The comment one screen up says the two are different tooltips; it is
      // right, and the same title node is what makes them look alike.
      maxWidthPx={180}
      data-testid="item-status-overlay"
      triggerClassName={cn("absolute top-2.5 left-2.5 z-10", className)}
    >
      <StatusChip kind={badge.kind} label={badge.label} />
    </MuiTooltip>
  );
}

/**
 * The details page's header badge — the third painter, and the donor's third
 * component (`Header/Badges.tsx:26-40`).
 *
 * It is neither of the two above: the table's chip has no tooltip and the
 * card's is absolutely positioned over a picture, while this one sits in the
 * flow of a header row. Core here is the donor's plain `Typography` pill —
 * `4px 10px`, 4px radius, body2 on `primary.main` — not the card's ribbon with
 * its two squared corners.
 *
 * The rule it paints is `itemDetailStatusBadge`'s: pending wins, then core, and
 * no Not Recommended arm at all.
 */
export function ItemDetailStatusBadge({ item, className }: ItemDetailBadgeProps) {
  const badge = itemDetailStatusBadge(item);
  if (badge === null) return null;

  if (badge.kind === "core") {
    return (
      <span
        data-testid="pdp-status-badge"
        className={cn(
          "bg-lennar-blue-600 rounded-[4px] px-2.5 py-1 text-sm font-normal text-white",
          className,
        )}
      >
        {badge.label}
      </span>
    );
  }

  // `ItemStatusPendingChip` carries the tooltip on this screen, where the
  // table's `ItemPendingLabel` does not — the donor's own split, kept.
  return (
    <MuiTooltip
      title={PENDING_TOOLTIP}
      maxWidthPx={180}
      data-testid="pdp-status-badge"
      // **A named parity break — owner-ruled, #430 D1 (both detail pages).**
      //
      // The donor's chip is `position: absolute; top: 10; left: 10; zIndex: 1`
      // (`item-status-chip.tsx:15-19`) and *nothing on the details page
      // positions it*: `Badges`' Box, `HeaderContainer`'s Stack, the island Box
      // and `PageContainer`'s Container are all static, and the only
      // `position: relative` in the island tree is the image column, below the
      // header. So in modsy the chip escapes to the initial containing block
      // and lands 10px from the top-left of the *page*, over the gutter, while
      // the header row collapses because its only child left the flow.
      //
      // Those offsets were written for `ItemCard`, which *is* positioned — on a
      // card they put the chip in the card's own corner. The details page
      // reusing the same chip unpositioned is the donor's accident, and it was
      // shipped faithfully here (186ab29) until the owner ruled it out: the
      // break covers both detail pages, and only them, since this component has
      // no other caller — the table's chip is `ItemStatusLabel` and keeps its
      // own placement.
      //
      // So the chip sits in the header row's flow, where `Badges`' flex row
      // would have put it had the donor not positioned it out. Everything else
      // about it stays donor-exact: the label, the `#f9e1b4` ground, the black
      // text, the 0.8rem type, the clock icon in `#E38D0D`, and the 180px
      // tooltip.
      triggerClassName={className}
    >
      <StatusChip kind={badge.kind} label={badge.label} />
    </MuiTooltip>
  );
}

/**
 * The EI programme's own mark, laid over the item's picture — **card view
 * only**, and on the same gate as the chips below.
 *
 * The donor draws it as `EITiersLogo`, absolutely positioned inside the image
 * column at `top: 75, left: 0` with a 50px height, so it overlaps the lower-left
 * of the picture rather than sitting beside it. The caller supplies the
 * positioning context; this positions itself.
 *
 * `pointer-events-none` is the donor's too: the mark sits over the picture, and
 * without it the mark would swallow clicks meant for what is underneath.
 */
export function ItemEiLogo({
  item,
  placement = "card",
  className,
}: ItemDetailBadgeProps & { placement?: ItemEiLogoPlacement }) {
  if (!itemShowsEiMark(item, placement)) return null;

  return (
    <img
      src="/ei-tiers.png"
      // `EITiersLogo.tsx:16` — the donor's own `alt` ("EI Packages" since timmy#3333),
      // on every surface it draws.
      //
      // An earlier cut here made the mark decorative (`alt="" aria-hidden`), on
      // the reasoning that the named tier chips sit directly beneath it and a
      // reader would be told the same thing twice. That reasoning is false on
      // the anonymous product page, which is the surface #309 adds: `Badges
      // .tsx:20-22` gates the chips on `!isPublic`, so a visitor who cannot see
      // the picture is left with no EI signal at all. Rather than make the
      // label conditional — a distinction the donor does not draw — it is
      // simply restored, which is what modsy says on both surfaces anyway.
      alt="EI Packages"
      data-testid="item-ei-logo"
      className={cn(
        "pointer-events-none absolute z-[2] w-auto object-contain",
        EI_LOGO_PLACEMENTS[placement],
        className,
      )}
    />
  );
}

/**
 * The item's EI tiers, one pale chip each — **card view only.**
 *
 * A list, and a named one: out of context "EI First-Time Value" is a phrase, not
 * an attribute of the item above it.
 */
export function ItemEiTierChips({ item, className }: ItemBadgeProps) {
  const tiers = itemEiTiers(item);
  // Nothing at all rather than an empty `<ul>`: in a grid of cards an empty
  // element still holds its line of space open, so cards with no tiers would
  // sit a row taller than they need to and the grid would look ragged.
  if (tiers.length === 0) return null;

  return (
    // Full width and left-aligned, wrapping — `EITiers`'s own `Box`, which is
    // `width: '100%'` with `alignItems: 'flex-start'` and `pt: 2 / pb: 0.5`.
    // Centring them (this port's first pass) reads as a caption under the
    // picture rather than a row of attributes belonging to the item.
    <ul
      aria-label="EI tiers"
      // `gap: { xs: .25, sm: .5 }` — 2px below 600px, 4px above.
      className={cn("flex w-full flex-wrap gap-1 pt-4 pb-1 max-sm:gap-0.5", className)}
    >
      {tiers.map((tier) => (
        // `flexShrink: 0` (`EITiers.tsx:45`). The donor's chip *is* the flex
        // item; here the `<li>` is, so the rule belongs on it, or a column too
        // narrow for the chip squeezes it back down.
        //
        // And `flex`, so the row is the chip's own box rather than a line box
        // around it: the donor's chip is a direct flex item of its container
        // (`EITiers.tsx:39-51`), where an inline-block chip inside a block
        // `<li>` takes the inherited 24px strut and stands 1.5px taller than
        // the chip below 600px.
        <li key={tier.id} className="flex shrink-0">
          {/* One treatment across every tier — the donor gives its tier chips a
              single pale-blue fill too. The tier's *name* is what distinguishes
              them, so colouring them apart would invent a hierarchy the data
              does not carry. */}
          {/* The chip shrinks below 600px, all four of the donor's ways:
              `paddingX: {xs: .5, sm: 1}`, `paddingY: {xs: .25, sm: .5}` and
              `fontSize: {xs: '.85rem', sm: 'inherit'}` (`EITiers.tsx:10-17`).
              The line height is declared rather than left to Tailwind's
              `text-sm`, because the donor's is MUI's unitless `1.43` on
              `body2` (`theme.ts:180-184`): 13.6 × 1.43 = 19.448px.

              The **size is flat**, which the source does not say. `fontSize:
              { xs: '.85rem', sm: 'inherit' }` (`:17`) reads as 13.6px below
              600px and `body2`'s 14px above, and the running donor is 13.6px
              on a 19.448px line at 375, 599, 601, 1024 *and* 1440 — the
              `sm: 'inherit'` half never lands, though the chip's parent
              computes 16px. A first cut here took the declaration at its word
              and shipped `text-sm` above the boundary, which made the card's
              min-content 4.2px wider than modsy's and the grid's floored
              track 195.3px against its 191.1px.

              `w-min whitespace-nowrap` is `width: 'min-content'` with
              `whiteSpace: 'nowrap'` (`:12`, `:16`): a chip is exactly its text
              plus padding, and a container too narrow for it overflows rather
              than breaking the words. This is also what floors the donor's
              card — measured at 1024px, modsy holds "EI First-Time Premium" on
              one line at 157px in a 191px track, where this port wrapped it
              onto two lines in a 162px track and grew every card ~80px
              taller. #528 is what made it visible: four tracks at every width
              means the track falls under the widest chip below ~1100px. */}
          <span className="bg-catalog-ei inline-block w-min rounded-[4px] px-2 py-1 text-[0.85rem] leading-[1.43] whitespace-nowrap text-black max-sm:px-1 max-sm:py-0.5">
            {tier.name}
          </span>
        </li>
      ))}
    </ul>
  );
}
